// Historical performance data for different asset types (last 5 years)
export const generateAssetPerformance = (assetType, investedAmount, investmentDate) => {
  const now = new Date();
  const startDate = new Date(investmentDate);
  const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
  
  // Base performance patterns for different assets
  const performancePatterns = {
    stocks: {
      volatility: 0.02, // 2% daily volatility
      annualReturn: 0.15, // 15% annual return
      trend: 'growth'
    },
    mutualFunds: {
      volatility: 0.012, // 1.2% daily volatility
      annualReturn: 0.12, // 12% annual return
      trend: 'steady'
    },
    gold: {
      volatility: 0.008, // 0.8% daily volatility
      annualReturn: 0.08, // 8% annual return
      trend: 'stable'
    },
    fixedDeposit: {
      volatility: 0.001, // 0.1% daily volatility
      annualReturn: 0.07, // 7% annual return
      trend: 'linear'
    }
  };

  const pattern = performancePatterns[assetType] || performancePatterns.stocks;
  const dailyReturn = Math.pow(1 + pattern.annualReturn, 1/365) - 1;
  
  // Generate realistic performance data
  let currentValue = investedAmount;
  const performanceData = [];
  
  for (let i = 0; i <= Math.min(daysDiff, 1825); i++) { // Max 5 years
    if (i === 0) {
      performanceData.push({
        date: new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000),
        value: investedAmount,
        return: 0
      });
      continue;
    }
    
    // Add trend-based growth
    let growth = dailyReturn;
    
    // Add realistic volatility
    const randomFactor = (Math.random() - 0.5) * 2 * pattern.volatility;
    growth += randomFactor;
    
    // Add market cycle effects
    const cycleEffect = Math.sin(i / 365 * 2 * Math.PI) * 0.005; // Annual cycle
    growth += cycleEffect;
    
    currentValue *= (1 + growth);
    
    performanceData.push({
      date: new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000),
      value: currentValue,
      return: ((currentValue - investedAmount) / investedAmount) * 100
    });
  }
  
  return performanceData;
};

// Generate 5-year historical data for asset types
export const getFiveYearPerformance = (assetType) => {
  const endDate = new Date();
  const startDate = new Date(endDate.getTime() - 5 * 365 * 24 * 60 * 60 * 1000);
  
  const monthlyData = [];
  let baseValue = 100; // Starting at 100 for percentage calculation
  
  const patterns = {
    stocks: { volatility: 0.15, annualReturn: 0.15 },
    mutualFunds: { volatility: 0.10, annualReturn: 0.12 },
    gold: { volatility: 0.08, annualReturn: 0.08 },
    fixedDeposit: { volatility: 0.02, annualReturn: 0.07 }
  };
  
  const pattern = patterns[assetType] || patterns.stocks;
  const monthlyReturn = Math.pow(1 + pattern.annualReturn, 1/12) - 1;
  
  for (let i = 0; i < 60; i++) { // 60 months = 5 years
    const date = new Date(startDate.getTime() + i * 30 * 24 * 60 * 60 * 1000);
    
    if (i === 0) {
      monthlyData.push({ date, value: baseValue });
      continue;
    }
    
    // Monthly growth with volatility
    let growth = monthlyReturn;
    const randomFactor = (Math.random() - 0.5) * pattern.volatility;
    growth += randomFactor;
    
    // Market events simulation
    if (i === 15) growth -= 0.08; // Market correction
    if (i === 35) growth -= 0.12; // Major dip
    if (i === 45) growth += 0.15; // Recovery rally
    
    baseValue *= (1 + growth);
    monthlyData.push({ date, value: baseValue });
  }
  
  return monthlyData;
};